<template>
  <div class="container">
    <div
      v-for="(content, index) in infoWindowContents"
      :key="index"
      class="content"
    >
      <div
        v-if="parcelTitle(content)"
        class="infoWindowHeader"
      >
        <InfoWindowName
          :info-window-name="parcelTitle(content)"
          data-testid="title"
        />
      </div>
      <div class="infoWindowBody">
        <DisplayFields :display-fields="content.displayFields" />
        <np-action-btn
          :disabled="!selectedOverlay"
          data-testid="add-to-overlay-button"
          class="float-right mt-2"
          @click="addToOverlay(content)"
        >
          Add to Overlay
        </np-action-btn>
      </div>
    </div>
  </div>
</template>

<script>
import {mapMutations, mapState} from 'vuex';

import InfoWindowName from './sections/Header/InfoWindowName.vue';
import DisplayFields from './DisplayFields/DisplayFields.vue';
import {gmAPI} from '@/services/MapService';
import {geohashForLocation} from 'geofire-common';
import {extractField} from '@/lib/classes/InfoWindowContent';

/** @typedef {import('@/lib/classes/InfoWindowContent').InfoWindowContent} InfoWindowContent */

export default {
  name: 'ParcelInfoWindow',
  components: {
    InfoWindowName,
    DisplayFields,
  },
  props: {
    infoWindowContents: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState(['selectedOverlay']),
  },
  methods: {
    ...mapMutations(['setSelectedMenuItem']),
    ...mapMutations('infowindow', ['clearInfoWindowContents']),
    /**
     * Cleans up the title for a parcel.
     * @param {InfoWindowContent} content The parcel's InfoWindowContent
     * @return {string} The title to be displayed.
     */
    parcelTitle(content) {
      return content?.title?.trim() ?? '';
    },
    /**
     * Adds a parcel to the selected overlay, then closes the InfoWindow and opens the Overlays menu.
     * @param {InfoWindowContent} content The parcel's InfoWindowContent.
     */
    async addToOverlay(content) {
      const geom = content?.metadata?.geom;
      if (!this.selectedOverlay || !geom) {
        console.warn(`ParcelInfoWindow.addToOverlay: failed because either no overlay selected or no parcel geom`);
        return;
      }
      try {
        let bounds = new gmAPI.LatLngBounds();
        // Assign each polygon in the parcel to a 'polys' object to store in Firestore.
        const polys = {};
        geom.forEach((geo, i) => {
          const path = [];
          geo.getPath().forEach((point) => {
            path.push({lat: point.lat(), lng: point.lng()});
            bounds.extend(point);
          });
          polys[i] = path;
        });
        // Calculate geohash and normalize bounds to JSON.
        const center = bounds.getCenter();
        const geohash = geohashForLocation([center.lat(), center.lng()]);
        bounds = bounds.toJSON();
        // Generate feature data.
        const data = {
          name: extractField(content, 'Owner')?.value ?? 'Parcel',
          displayFields: content.displayFields.filter((df) => df.value || df.displayFields),
          bounds,
          geohash,
          graphicData: {
            featureType: 'polygon',
            clickable: true,
            bounds,
            polys,
          },
        };
        // Create the feature in the selected overlay.
        await this.selectedOverlay.newFeature(data);
      } catch (err) {
        console.error(`Failed to add parcel to overlay: ${err}`);
        return;
      }
      // Unhighlight the parcel after it has been added to the overlay.
      content.host.setHighlight(false);
      // If we succeeded, close the InfoWindow and show the overlay.
      this.clearInfoWindowContents();
      this.setSelectedMenuItem('overlays');
    },
  },
};
</script>

<style scoped lang="scss">
.container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}
.content:not(:last-child)::after {
  content: " ";
  position: absolute;
  left: 0;
  height: 54px;
  display: block;
  width: 100%;
  border-bottom: 3px solid main.$lightGray;
}
</style>
